import UpgradeTrack, {
  colorByTrack,
  upgradeTracks,
  upgradeTrackToDifficulty,
} from 'app/frontend/types/custom/UpgradeTrack'
import { ColumnDefWithFilters, ColumnGroup } from '../types'
import { useSimpleColumns } from '../utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { crestTypes } from 'app/frontend/types/custom/CrestType'
import {
  itemEquipmentSlots,
  itemSlotLabels,
} from 'app/frontend/types/custom/ItemSlot'
import AuditFilter, { useAuditFilterState } from '../components/AuditFilter'
import { MultiSelect } from '@/components/ui/multi-select'
import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { usePageInfo } from 'app/frontend/base/config/queries'

export const useGearUpgradesColumns = (): ColumnDefWithFilters => {
  const { value: enabledTracks, setValue: setEnabledTracks } =
    useAuditFilterState<UpgradeTrack[]>('upgradeTracks', [], upgradeTracks)

  const {
    data: { currentSeason },
  } = usePageInfo()

  const collectionGroups: ColumnGroup[] = useMemo(
    () => [
      {
        id: 'items_by_track',
        header: 'Equipped items by track',
        rankFields: null,
        subColumns: upgradeTracks
          .filter((track) => track !== 'None')
          .map((track) => ({
            id: track as string,
            header: () => (
              <Tooltip>
                <TooltipTrigger>{track.charAt(0)}</TooltipTrigger>
                <TooltipContent>{track} track</TooltipContent>
              </Tooltip>
            ),
            field: `${upgradeTrackToDifficulty[track]}_track_items`,
            cellWrapper: (value) =>
              value === '-' ? (
                ''
              ) : (
                <span className={colorByTrack[track]}>{value}</span>
              ),
          })),
      },
      {
        id: 'crests_earned',
        header: 'Crests earned',
        rankFields: [
          'gilded_crests',
          'runed_crests',
          'carved_crests',
          'weathered_crests',
        ],
        rankWeights: {
          gilded_crests: 1000,
          runed_crests: 10,
          carved_crests: 2,
        },
        subColumns: crestTypes.map((crest) => ({
          id: crest as string,
          header: crest,
          field: `${crest.toLowerCase()}_crests`,
        })),
      },
      {
        id: 'upgrades_missing',
        header: 'Upgrades',
        rankInverted: true,
        subColumns: [
          {
            id: 'total_upgrades_missing',
            header: 'Missing',
            field: 'total_upgrades_missing',
          },
        ],
      },
      {
        id: 'upgrades_by_slot',
        header: 'Upgrade steps',
        rankFields: null,
        subColumns: itemEquipmentSlots.map((slot) => ({
          id: slot as string,
          header: () => <span>{itemSlotLabels[slot]}</span>,
          field: `upgrade_level_${slot}`,
          cellWrapper: (value, member) => {
            const [step, total] = String(value).split('/').map(Number)
            const track = member.gear?.[slot]?.upgrade_level?.track

            const valueToShow =
              enabledTracks.length === 0
                ? value
                : enabledTracks.includes(track as UpgradeTrack)
                ? value
                : ''

            return (
              <Tooltip>
                <TooltipTrigger
                  className={cn(
                    'whitespace-nowrap',
                    valueToShow === '-'
                      ? ''
                      : step === total
                      ? 'text-success-foreground'
                      : 'text-destructive-foreground',
                  )}
                >
                  {valueToShow}
                </TooltipTrigger>
                {track === 'None' ? null : (
                  <TooltipContent>
                    {track ? (
                      <span className={colorByTrack[track]}>{track}</span>
                    ) : (
                      'Unknown track'
                    )}
                  </TooltipContent>
                )}
              </Tooltip>
            )
          },
        })),
      },
    ],
    [currentSeason, enabledTracks],
  )

  const columns = useSimpleColumns(collectionGroups, [enabledTracks])

  return {
    columns,
    initialSortBy: [{ id: 'Gilded', desc: true }],
    filters: [
      <AuditFilter label='Count missing steps from' key='track'>
        <MultiSelect<UpgradeTrack>
          options={upgradeTracks.slice(0, 4).map((track) => ({
            value: track,
            label: <span className={colorByTrack[track]}>{track}</span>,
          }))}
          placeholder='All tracks'
          value={enabledTracks}
          onChange={(value) => setEnabledTracks(value)}
        />
      </AuditFilter>,
    ],
  }
}
